import { DialogActions, DialogContent } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useCustomNavigation } from "hooks/useCustomNavigation";
import { useQueryUpdater } from "hooks/useQueryUpdater";
import { ReactNode, useCallback, useMemo } from "react";
import {has} from "lodash";
import { PopupForm } from "components/Admin/PopupForm/PopupForm";
import { SubmitButton } from "components/Admin/Form/components/SubmitButton/SubmitButton";
import {useNotify, useResourceContext, useTranslate} from "react-admin";

export interface ActionFormPopupProps<TDefaultValue, TProps, TResult> {
  show?: boolean;
  onClose?: () => void;
  queryKey?: string;
  submitQuery: (props: { data: TProps }) => Promise<TResult>;
  updateCacheFn?: (data: TResult) => void;
  onSuccess?: (data: TResult) => void;
  onSuccessNavigate?: string;
  label: string;
  submitButtonText?: string;
  successMessage?: string;
  defaultValue?: any;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  zIndex?: number;
  isLoading?: boolean;
  renderButtons?: (loading: boolean) => ReactNode;
  children?: React.ReactNode;
  convertDefaultValue?: (data: TResult) => TDefaultValue;
  TransitionComponent?: React.JSXElementConstructor<any>;
  fullScreen?: boolean;
}

export const ActionFormPopup = <TDefaultValue, TProps, TResult>(
  props: ActionFormPopupProps<TDefaultValue, TProps, TResult>
) => {
  const { navigate, state } = useCustomNavigation({ delay: 300 });

  const t = useTranslate();

  const notify = useNotify();

  const resource = useResourceContext();

  const queryKey = props.queryKey ?? resource;

  if (!queryKey) {
    throw new Error("Query key or resource key is required");
  }

  const { updateEntity } = useQueryUpdater<TResult>({
    queryKey,
  });

  const convertData = useCallback(
    (data: any) => {
      if (!data) {
        return data;
      }

      if (!props.convertDefaultValue) {
        return data;
      }

      return props.convertDefaultValue(data);
    },
    [props]
  );

  const mutation = useMutation({
    mutationFn: (formData: any) => {
      return props.submitQuery({ data: formData });
    },
    onSuccess: (data) => {
      notify(props.successMessage ?? t("ra.notification.updated"), {
        type: "success",
      });

      if (props.updateCacheFn) {
        props.updateCacheFn(data);
      } else {
        updateEntity((entity) => {
          if (!has(entity, "id") || !has(data, "id")) {
            return entity;
          }

          if (entity.id !== data.id) {
            return entity;
          }

          return data;
        });
      }

      props.onSuccess?.(data);

      if (props.onSuccessNavigate) {
        navigate(props.onSuccessNavigate);
        return;
      }

      navigate("..");
    },
    onError: (error) => {
      console.error(error);
      notify(error?.message, {type: "error" });
    },
  });

  const show = useMemo(() => {
    if (props.show !== undefined) {
      return props.show;
    }

    return state === "show";
  }, [props.show, state]);

  const onClose = useCallback(() => {
    if (props.onClose) {
      props.onClose();
      return;
    }

    navigate("..");
  }, [props, navigate]);

  return (
    <PopupForm
      show={show}
      maxWidth={props.maxWidth}
      onClose={onClose}
      label={props.label}
      onSubmit={mutation.mutate}
      error={mutation.error}
      isLoading={props.isLoading || mutation.isPending}
      zIndex={props.zIndex ?? 200}
      defaultValues={convertData(props.defaultValue)}
      TransitionComponent={props.TransitionComponent}
      fullScreen={props.fullScreen}
    >
      <DialogContent>{props.children}</DialogContent>
      <DialogActions>
        {props.renderButtons?.(props.isLoading || mutation.isPending)}
        <SubmitButton color="primary" variant="contained">
          {props.submitButtonText ?? t("ra.actions.save")}
        </SubmitButton>
      </DialogActions>
    </PopupForm>
  );
};